// ── A run-twice problem as the contestant reads it (`EX-RTW03`) ─────────────
// Boards D to F of the Wave 35 plan. The facts every screen draws from: which
// stage a test stopped in, what each of the three cells says, and each run's
// figures. A standard problem never reaches this file's answers: every
// function here returns `null` (or an empty list) unless a row or a run says
// it ran twice, so a standard page, run panel and result stay what they were.

import type {
  RunTwiceStage,
  RunTwiceSummary,
  SampleRunCaseResult,
  StudentTestResult,
} from "@/features/quiz/coding-task-api";

export type { RunTwiceStage, RunTwiceSummary };

/** One cell of a result row: passed, failed, or never reached. */
export type StageCell = "pass" | "fail" | "skip";

/** The three cells of one run-twice test row: run 1, the jury's check, run 2. */
export type StageCells = { first: StageCell; bridge: StageCell; second: StageCell };

/**
 * A row's cells from its verdict and where it stopped. A row stops in the
 * stage that failed it, so every stage before is a pass and every one after
 * was never reached. An accepted row passed all three; a skipped one ran none.
 */
export function stageCellsOf(row: Pick<StudentTestResult, "verdict" | "stage">): StageCells | null {
  if (row.stage === undefined && row.verdict !== "skipped") return null;
  if (row.verdict === "skipped") return { first: "skip", bridge: "skip", second: "skip" };
  if (row.verdict === "accepted") return { first: "pass", bridge: "pass", second: "pass" };
  switch (row.stage) {
    case "first":
      return { first: "fail", bridge: "skip", second: "skip" };
    case "bridge":
      return { first: "pass", bridge: "fail", second: "skip" };
    default:
      return { first: "pass", bridge: "pass", second: "fail" };
  }
}

/** Whether these per-test rows belong to a run-twice problem. */
export function hasRunTwiceRows(rows: readonly StudentTestResult[] | null | undefined): boolean {
  return !!rows && rows.some((row) => row.stage !== undefined);
}

/**
 * The summary a result card draws: the server's own on a contest row
 * (`count_only` names no test), or folded from the per-test rows where the
 * policy sends them (practice, a staff rehearsal). `null` on a standard
 * problem, whatever the surface.
 */
export function runTwiceSummaryOf(view: {
  testResults: readonly StudentTestResult[] | null;
  runTwice?: RunTwiceSummary | null;
}): RunTwiceSummary | null {
  if (view.runTwice) return view.runTwice;
  const rows = view.testResults;
  if (!rows || !hasRunTwiceRows(rows)) return null;
  const failed = rows.find(
    (row) => row.stage !== undefined && row.verdict !== "accepted" && row.verdict !== "skipped",
  );
  const runs: { timeMs?: number; memoryKb?: number }[] = [];
  for (const row of rows) {
    (row.runs ?? []).forEach((run, index) => {
      const into = (runs[index] ??= {});
      if (run.timeMs !== undefined) into.timeMs = Math.max(into.timeMs ?? 0, run.timeMs);
      if (run.memoryKb !== undefined) into.memoryKb = Math.max(into.memoryKb ?? 0, run.memoryKb);
    });
  }
  return { ...(failed?.stage !== undefined ? { stage: failed.stage } : {}), runs };
}

/** What one stage of a sample run came to, as its card in the run panel says it. */
export type StageState = "waiting" | "running" | "ok" | "fail" | "skip" | "unchecked";

export type SampleStageStates = { first: StageState; bridge: StageState; second: StageState };

/**
 * The run panel's three cards for one case. `running` lights run 1 while the
 * judge works (the server answers in one go, so nothing finer is true);
 * `null` result is the idle panel. Run 2's card reads the case's own
 * comparison: `unchecked` when nothing honest compared it (custom input, a
 * problem with its own checker).
 */
export function sampleStageStates(result: SampleRunCaseResult | null, running: boolean): SampleStageStates {
  if (running) return { first: "running", bridge: "waiting", second: "waiting" };
  if (!result || !result.stages) return { first: "waiting", bridge: "waiting", second: "waiting" };
  const { first, bridge, second } = result.stages;
  const firstOk = first !== null && first.outcome === "completed";
  if (!firstOk) return { first: "fail", bridge: "skip", second: "skip" };
  if (!bridge || bridge.status !== "ok") return { first: "ok", bridge: "fail", second: "skip" };
  if (!second) return { first: "ok", bridge: "ok", second: "skip" };
  if (second.outcome !== "completed" || result.passed === false) return { first: "ok", bridge: "ok", second: "fail" };
  return { first: "ok", bridge: "ok", second: result.passed === true ? "ok" : "unchecked" };
}
